<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  agentConfig: {
    type: Object,
    required: true,
  },
  personaConfig: {
    type: Object,
    required: true,
  },
  behaviorConfig: {
    type: Object,
    required: true,
  },
  workflowAgentsConfig: {
    type: Object,
    default: () => ({
      triagem_name: 'Lia',
      triagem_avatar: 'woman-blonde',
      vendas_name: 'Anna',
      vendas_avatar: 'woman-brunette',
      financeiro_name: 'Valentina',
      financeiro_avatar: 'woman-redhead',
      suporte_name: 'Bianca',
      suporte_avatar: 'woman-black',
    }),
  },
  testModeEnabled: {
    type: Boolean,
    default: false,
  },
  agent: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits([
  'update:agentConfig',
  'update:personaConfig',
  'update:workflowAgentsConfig',
]);

// Check if it's LIA 1.4/2.1 (same UI) or 3.1+ (predefined in workflow)
const isLia14 = computed(() => {
  const version = props.agent?.lia_version || '';
  return version.includes('1.4') || version === '';
});

const isLia21 = computed(() => {
  const version = props.agent?.lia_version || '';
  return version.includes('2.1');
});

const isLia31 = computed(() => {
  const version = props.agent?.lia_version || '';
  return version.includes('3.1');
});

const { t } = useI18n();

// Avatar options
const avatarOptions = computed(() => [
  {
    id: 'woman-blonde',
    image: '/images/avatars/woman-blonde.svg',
    label: t('LIA_AGENTS.START.AVATAR_BLONDE'),
  },
  {
    id: 'woman-brunette',
    image: '/images/avatars/woman-brunette.svg',
    label: t('LIA_AGENTS.START.AVATAR_BRUNETTE'),
  },
  {
    id: 'woman-redhead',
    image: '/images/avatars/woman-redhead.svg',
    label: t('LIA_AGENTS.START.AVATAR_REDHEAD'),
  },
  {
    id: 'woman-black',
    image: '/images/avatars/woman-black.svg',
    label: t('LIA_AGENTS.START.AVATAR_BLACK'),
  },
  {
    id: 'man-blonde',
    image: '/images/avatars/man-blonde.svg',
    label: t('LIA_AGENTS.START.AVATAR_BLONDE_M'),
  },
  {
    id: 'man-brunette',
    image: '/images/avatars/man-brunette.svg',
    label: t('LIA_AGENTS.START.AVATAR_BRUNETTE_M'),
  },
  {
    id: 'man-redhead',
    image: '/images/avatars/man-redhead.svg',
    label: t('LIA_AGENTS.START.AVATAR_REDHEAD_M'),
  },
  {
    id: 'man-black',
    image: '/images/avatars/man-black.svg',
    label: t('LIA_AGENTS.START.AVATAR_BLACK_M'),
  },
  {
    id: 'bot',
    icon: 'i-lucide-bot',
    label: t('LIA_AGENTS.START.AVATAR_BOT'),
    color: 'text-woot-500',
  },
  {
    id: 'bot-ai',
    icon: 'i-lucide-sparkles',
    label: t('LIA_AGENTS.START.AVATAR_AI'),
    color: 'text-amber-500',
  },
]);

// All role options
const allRoleOptions = [
  {
    id: 'atendente',
    label: t('LIA_AGENTS.START.ROLE_ATENDENTE'),
    desc: t('LIA_AGENTS.START.ROLE_ATENDENTE_DESC'),
  },
  {
    id: 'vendedor',
    label: t('LIA_AGENTS.START.ROLE_VENDEDOR'),
    desc: t('LIA_AGENTS.START.ROLE_VENDEDOR_DESC'),
  },
  {
    id: 'suporte',
    label: t('LIA_AGENTS.START.ROLE_SUPORTE'),
    desc: t('LIA_AGENTS.START.ROLE_SUPORTE_DESC'),
  },
  {
    id: 'financeiro',
    label: t('LIA_AGENTS.START.ROLE_FINANCEIRO'),
    desc: t('LIA_AGENTS.START.ROLE_FINANCEIRO_DESC'),
  },
  {
    id: 'recepcionista',
    label: t('LIA_AGENTS.START.ROLE_RECEPCIONISTA'),
    desc: t('LIA_AGENTS.START.ROLE_RECEPCIONISTA_DESC'),
  },
  {
    id: 'consultor',
    label: t('LIA_AGENTS.START.ROLE_CONSULTOR'),
    desc: t('LIA_AGENTS.START.ROLE_CONSULTOR_DESC'),
  },
  {
    id: 'sac',
    label: t('LIA_AGENTS.START.ROLE_SAC'),
    desc: t('LIA_AGENTS.START.ROLE_SAC_DESC'),
  },
  {
    id: 'pos_venda',
    label: t('LIA_AGENTS.START.ROLE_POS_VENDA'),
    desc: t('LIA_AGENTS.START.ROLE_POS_VENDA_DESC'),
  },
];

// LIA 1.4: only Atendente and Recepcionista
const LIA_14_ROLES = ['atendente', 'recepcionista'];

const roleOptions = computed(() => {
  if (isLia14.value) {
    return allRoleOptions.filter(r => LIA_14_ROLES.includes(r.id));
  }
  return allRoleOptions;
});

// Specialty options
const specialtyOptions = computed(() => [
  {
    id: 'vendas',
    label: t('LIA_AGENTS.START.SPECIALTY_VENDAS'),
    color:
      'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  },
  {
    id: 'suporte',
    label: t('LIA_AGENTS.START.SPECIALTY_SUPORTE'),
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  {
    id: 'financeiro',
    label: t('LIA_AGENTS.START.SPECIALTY_FINANCEIRO'),
    color:
      'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  },
  {
    id: 'agendamento',
    label: t('LIA_AGENTS.START.SPECIALTY_AGENDAMENTO'),
    color:
      'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  },
  {
    id: 'informacoes',
    label: t('LIA_AGENTS.START.SPECIALTY_INFORMACOES'),
    color: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
  },
  {
    id: 'reclamacoes',
    label: t('LIA_AGENTS.START.SPECIALTY_RECLAMACOES'),
    color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  {
    id: 'tecnico',
    label: t('LIA_AGENTS.START.SPECIALTY_TECNICO'),
    color:
      'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  },
  {
    id: 'produtos',
    label: t('LIA_AGENTS.START.SPECIALTY_PRODUTOS'),
    color: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
  },
  {
    id: 'servicos',
    label: t('LIA_AGENTS.START.SPECIALTY_SERVICOS'),
    color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
  },
  {
    id: 'faq',
    label: t('LIA_AGENTS.START.SPECIALTY_FAQ'),
    color:
      'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  },
]);

// Languages available
const languages = computed(() => [
  { id: 'pt-BR', name: t('LIA_AGENTS.START.LANG_PT_BR'), flag: '🇧🇷' },
  { id: 'es', name: t('LIA_AGENTS.START.LANG_ES'), flag: '🇪🇸' },
  { id: 'en', name: t('LIA_AGENTS.START.LANG_EN'), flag: '🇺🇸' },
]);

// Behavior options for display
const behaviorOptions = computed(() => ({
  tone: [
    { value: 'direct', label: t('LIA_AGENTS.BEHAVIOR.TONE_DIRECT') },
    { value: 'friendly', label: t('LIA_AGENTS.BEHAVIOR.TONE_FRIENDLY') },
    {
      value: 'enthusiastic',
      label: t('LIA_AGENTS.BEHAVIOR.TONE_ENTHUSIASTIC'),
    },
  ],
  response_length: [
    { value: 'short', label: t('LIA_AGENTS.BEHAVIOR.RESPONSE_SHORT') },
    { value: 'medium', label: t('LIA_AGENTS.BEHAVIOR.RESPONSE_MEDIUM') },
    { value: 'detailed', label: t('LIA_AGENTS.BEHAVIOR.RESPONSE_DETAILED') },
  ],
  formality: [
    { value: 'formal', label: t('LIA_AGENTS.BEHAVIOR.FORMALITY_FORMAL') },
    { value: 'neutral', label: t('LIA_AGENTS.BEHAVIOR.FORMALITY_NEUTRAL') },
    { value: 'casual', label: t('LIA_AGENTS.BEHAVIOR.FORMALITY_CASUAL') },
  ],
}));

// Helper to get avatar display info
const getAvatarDisplay = avatarId => {
  const avatar = avatarOptions.value.find(a => a.id === avatarId);
  return avatar || { icon: 'i-lucide-bot', color: 'text-woot-500' };
};

// Update agent config field
const updateAgentConfig = (field, value) => {
  emit('update:agentConfig', { ...props.agentConfig, [field]: value });
};

// Update persona config field
const updatePersonaConfig = (field, value) => {
  emit('update:personaConfig', { ...props.personaConfig, [field]: value });
};

// Update workflow agents config field
const updateWorkflowAgentName = (field, value) => {
  emit('update:workflowAgentsConfig', {
    ...props.workflowAgentsConfig,
    [field]: value,
  });
};

// Handle avatar upload
const handleAvatarUpload = event => {
  const file = event.target.files[0];
  if (!file) return;

  if (!file.type.startsWith('image/')) return;

  const reader = new FileReader();
  reader.onload = e => {
    emit('update:personaConfig', {
      ...props.personaConfig,
      customAvatar: e.target.result,
      avatar: 'custom',
    });
  };
  reader.readAsDataURL(file);
};

// Clear custom avatar
const clearCustomAvatar = () => {
  emit('update:personaConfig', {
    ...props.personaConfig,
    customAvatar: '',
    avatar: 'bot',
  });
};

// Select avatar
const selectAvatar = avatarId => {
  emit('update:personaConfig', {
    ...props.personaConfig,
    avatar: avatarId,
    customAvatar: '',
  });
};

// Toggle specialty
const toggleSpecialty = specialtyId => {
  const specialties = props.personaConfig.specialties || [];
  const index = specialties.indexOf(specialtyId);
  if (index === -1) {
    updatePersonaConfig('specialties', [...specialties, specialtyId]);
  } else {
    updatePersonaConfig(
      'specialties',
      specialties.filter(s => s !== specialtyId)
    );
  }
};

// Toggle language
const toggleLanguage = langId => {
  const langs = props.agentConfig.languages || [];
  const index = langs.indexOf(langId);

  let newLangs;
  if (index === -1) {
    // Add the language
    newLangs = [...langs, langId];
  } else if (langs.length > 1) {
    // Remove the language (only if more than 1 selected)
    newLangs = langs.filter(l => l !== langId);
  } else {
    // Can't remove the only language
    return;
  }

  // Update both languages array and primary language in a single emit
  emit('update:agentConfig', {
    ...props.agentConfig,
    languages: newLangs,
    language: newLangs[0] || 'pt-BR',
  });
};
</script>

<template>
  <div class="space-y-6">
    <!-- Persona Preview Card (LIA 1.4) -->
    <div
      v-if="isLia14"
      class="bg-gradient-to-br from-woot-50 via-purple-50 to-pink-50 dark:from-woot-900/30 dark:via-purple-900/30 dark:to-pink-900/30 rounded-xl p-6 border border-woot-200 dark:border-woot-800"
    >
      <div class="flex items-start gap-4">
        <!-- Avatar Display -->
        <div
          class="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white dark:bg-n-solid-3 shadow-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
        >
          <img
            v-if="personaConfig.customAvatar"
            :src="personaConfig.customAvatar"
            alt="Avatar"
            class="w-full h-full object-cover"
          />
          <img
            v-else-if="getAvatarDisplay(personaConfig.avatar).image"
            :src="getAvatarDisplay(personaConfig.avatar).image"
            :alt="getAvatarDisplay(personaConfig.avatar).label"
            class="w-full h-full object-cover"
          />
          <span
            v-else
            class="size-10 sm:size-12"
            :class="[
              getAvatarDisplay(personaConfig.avatar).icon,
              getAvatarDisplay(personaConfig.avatar).color,
            ]"
          />
        </div>
        <div class="flex-1 min-w-0">
          <h2 class="text-xl sm:text-2xl font-bold text-n-slate-12 truncate">
            {{ agentConfig.name || t('LIA_AGENTS.START.IDENTITY_TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11 mt-0.5">
            {{
              roleOptions.find(r => r.id === personaConfig.role)?.label ||
              t('LIA_AGENTS.START.ROLE_ATENDENTE')
            }}
            •
            {{
              agentConfig.company_name ||
              t('LIA_AGENTS.GENERAL.COMPANY_NAME_PLACEHOLDER')
            }}
          </p>
          <p
            v-if="personaConfig.description"
            class="text-sm text-n-slate-11 mt-2 line-clamp-2"
          >
            {{ personaConfig.description }}
          </p>
          <!-- Specialties Tags -->
          <div
            v-if="personaConfig.specialties?.length > 0"
            class="flex flex-wrap gap-1.5 mt-3"
          >
            <span
              v-for="specId in personaConfig.specialties"
              :key="specId"
              class="px-2 py-0.5 text-xs font-medium rounded-full"
              :class="
                specialtyOptions.find(s => s.id === specId)?.color ||
                'bg-gray-100 text-gray-700'
              "
            >
              {{ specialtyOptions.find(s => s.id === specId)?.label }}
            </span>
          </div>
          <!-- Behavior Summary -->
          <div
            class="flex flex-wrap items-center gap-2 mt-3 text-xs text-n-slate-11"
          >
            <span>{{
              behaviorOptions.tone.find(t => t.value === behaviorConfig.tone)
                ?.label
            }}</span>
            <span class="text-n-slate-9">•</span>
            <span>{{
              behaviorOptions.response_length.find(
                r => r.value === behaviorConfig.response_length
              )?.label
            }}</span>
            <span class="text-n-slate-9">•</span>
            <span>{{
              behaviorOptions.formality.find(
                f => f.value === behaviorConfig.formality
              )?.label
            }}</span>
          </div>
        </div>
        <!-- LIA Version Badge -->
        <div class="hidden sm:block">
          <span
            class="px-3 py-1 text-xs font-semibold rounded-full bg-woot-100 text-woot-700 dark:bg-woot-900/50 dark:text-woot-300"
          >
            {{ agent?.lia_version || 'LIA 2.1' }}
          </span>
        </div>
      </div>
    </div>

    <!-- Agent Identity (LIA 1.4 only) -->
    <div v-if="isLia14" class="bg-n-solid-3 rounded-xl p-6">
      <h3
        class="text-lg font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
      >
        <span class="i-lucide-user-circle size-5 text-woot-500" />
        {{ t('LIA_AGENTS.START.IDENTITY_TITLE') }}
      </h3>
      <div class="space-y-4">
        <!-- Name -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
            {{ t('LIA_AGENTS.START.AGENT_NAME_LABEL') }}
          </label>
          <input
            :value="agentConfig.name"
            type="text"
            :placeholder="t('LIA_AGENTS.START.AGENT_NAME_PLACEHOLDER')"
            class="w-full px-4 py-2.5 bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500"
            @input="updateAgentConfig('name', $event.target.value)"
          />
        </div>

        <!-- Avatar -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ t('LIA_AGENTS.START.AVATAR_LABEL') }}
          </label>
          <div class="flex items-center gap-2 flex-wrap">
            <!-- Custom Avatar Upload -->
            <div class="relative flex-shrink-0">
              <label
                class="relative w-11 h-11 rounded-full border-2 border-dashed border-n-weak hover:border-woot-500 bg-n-solid-3 flex items-center justify-center cursor-pointer transition-all group overflow-hidden"
                :class="
                  personaConfig.avatar === 'custom'
                    ? 'border-woot-500 border-solid'
                    : ''
                "
              >
                <img
                  v-if="personaConfig.customAvatar"
                  :src="personaConfig.customAvatar"
                  alt="Avatar"
                  class="w-full h-full object-cover"
                />
                <span
                  v-else
                  class="i-lucide-upload size-5 text-n-slate-11 group-hover:text-woot-500"
                />
                <span
                  v-if="personaConfig.avatar === 'custom'"
                  class="absolute -top-0.5 -right-0.5 w-4 h-4 bg-woot-500 rounded-full flex items-center justify-center z-10"
                >
                  <span class="i-lucide-check size-2.5 text-white" />
                </span>
                <input
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="handleAvatarUpload"
                />
              </label>
              <button
                v-if="personaConfig.customAvatar"
                type="button"
                class="absolute -bottom-1 -right-1 w-5 h-5 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center z-20"
                @click="clearCustomAvatar"
              >
                <span class="i-lucide-x size-3 text-white" />
              </button>
            </div>

            <!-- Avatar Options -->
            <button
              v-for="avatar in avatarOptions"
              :key="avatar.id"
              type="button"
              class="relative p-0.5 border-2 rounded-full transition-all hover:scale-110 flex-shrink-0"
              :class="[
                personaConfig.avatar === avatar.id
                  ? 'border-woot-500'
                  : 'border-transparent hover:border-n-strong',
              ]"
              @click="selectAvatar(avatar.id)"
            >
              <span
                v-if="personaConfig.avatar === avatar.id"
                class="absolute -top-0.5 -right-0.5 w-4 h-4 bg-woot-500 rounded-full flex items-center justify-center z-10"
              >
                <span class="i-lucide-check size-2.5 text-white" />
              </span>
              <img
                v-if="avatar.image"
                :src="avatar.image"
                :alt="avatar.label"
                class="w-10 h-10 rounded-full object-cover"
              />
              <div
                v-else
                class="w-10 h-10 rounded-full flex items-center justify-center bg-n-solid-3"
              >
                <span :class="[avatar.icon, avatar.color]" class="size-5" />
              </div>
            </button>
          </div>
        </div>

        <!-- Role (only for LIA 1.4) -->
        <div v-if="isLia14">
          <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
            {{ t('LIA_AGENTS.START.ROLE_LABEL') }}
          </label>
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <button
              v-for="role in roleOptions"
              :key="role.id"
              type="button"
              class="relative flex flex-col items-start p-3 border-2 rounded-lg transition-all text-left"
              :class="[
                personaConfig.role === role.id
                  ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20'
                  : 'border-n-weak hover:border-n-strong',
              ]"
              @click="updatePersonaConfig('role', role.id)"
            >
              <span
                v-if="personaConfig.role === role.id"
                class="absolute top-1.5 right-1.5 i-lucide-check-circle size-4 text-woot-500"
              />
              <span class="text-sm font-medium text-n-slate-12">{{
                role.label
              }}</span>
              <span class="text-xs text-n-slate-11 line-clamp-1">{{
                role.desc
              }}</span>
            </button>
          </div>
        </div>

        <!-- Description -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
            {{ t('LIA_AGENTS.START.DESCRIPTION_LABEL') }}
          </label>
          <textarea
            :value="personaConfig.description"
            rows="2"
            :placeholder="t('LIA_AGENTS.START.DESCRIPTION_PLACEHOLDER')"
            class="w-full px-4 py-2.5 bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500 text-sm resize-none"
            @input="updatePersonaConfig('description', $event.target.value)"
          />
          <p class="mt-1 text-xs text-n-slate-11">
            {{ t('LIA_AGENTS.START.DESCRIPTION_HELP') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Specialties (only for LIA 1.4) -->
    <div v-if="isLia14" class="bg-n-solid-3 rounded-xl p-6">
      <h3
        class="text-lg font-semibold text-n-slate-12 mb-2 flex items-center gap-2"
      >
        <span class="i-lucide-sparkles size-5 text-woot-500" />
        {{ t('LIA_AGENTS.START.SPECIALTIES_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mb-4">
        {{ t('LIA_AGENTS.START.SPECIALTIES_SUBTITLE') }}
      </p>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="specialty in specialtyOptions"
          :key="specialty.id"
          type="button"
          class="px-3 py-1.5 text-sm font-medium rounded-full border-2 transition-all"
          :class="[
            personaConfig.specialties?.includes(specialty.id)
              ? specialty.color + ' border-transparent'
              : 'border-n-weak text-n-slate-11 hover:border-n-strong',
          ]"
          @click="toggleSpecialty(specialty.id)"
        >
          <span class="flex items-center gap-1.5">
            <span
              v-if="personaConfig.specialties?.includes(specialty.id)"
              class="i-lucide-check size-3.5"
            />
            {{ specialty.label }}
          </span>
        </button>
      </div>
      <p class="mt-3 text-xs text-n-slate-11">
        {{
          t('LIA_AGENTS.START.SPECIALTIES_COUNT', {
            count: personaConfig.specialties?.length || 0,
          })
        }}
      </p>
    </div>

    <!-- Workflow Agents (for LIA 3.1) -->
    <!-- eslint-disable vue/no-bare-strings-in-template -->
    <div v-if="isLia31" class="bg-n-solid-3 rounded-xl p-6">
      <h3
        class="text-lg font-semibold text-n-slate-12 mb-2 flex items-center gap-2"
      >
        <span class="i-lucide-bot size-5 text-woot-500" />
        {{ t('LIA_AGENTS.START.WORKFLOW_AGENTS_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mb-4">
        {{ t('LIA_AGENTS.START.WORKFLOW_AGENTS_SUBTITLE') }}
      </p>
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <!-- Triagem - Teal -->
        <div
          class="p-3 rounded-lg border border-n-weak bg-n-teal-3 dark:bg-n-teal-3"
        >
          <div class="flex items-center gap-2 mb-1">
            <span class="i-lucide-scan-search size-4 text-n-teal-11" />
            <span class="text-sm font-medium text-n-teal-11">Triagem</span>
          </div>
          <input
            :value="workflowAgentsConfig.triagem_name"
            type="text"
            placeholder="Nome"
            class="w-full px-0 py-0.5 text-xs bg-transparent border-0 border-b border-dashed border-n-teal-8/50 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-teal-8 font-medium"
            @input="
              updateWorkflowAgentName('triagem_name', $event.target.value)
            "
          />
          <p class="text-xs text-n-slate-11 mt-2">
            Identifica a necessidade do cliente
          </p>
        </div>
        <!-- Vendas - Green -->
        <div
          class="p-3 rounded-lg border border-n-weak bg-green-100 dark:bg-green-800"
        >
          <div class="flex items-center gap-2 mb-1">
            <span
              class="i-lucide-shopping-cart size-4 text-green-600 dark:text-green-50"
            />
            <span class="text-sm font-medium text-green-700 dark:text-green-50">
              Vendas
            </span>
          </div>
          <input
            :value="workflowAgentsConfig.vendas_name"
            type="text"
            placeholder="Nome"
            class="w-full px-0 py-0.5 text-xs bg-transparent border-0 border-b border-dashed border-green-600/50 dark:border-green-200/50 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-green-600 dark:focus:border-green-200 font-medium"
            @input="updateWorkflowAgentName('vendas_name', $event.target.value)"
          />
          <p class="text-xs text-n-slate-11 mt-2">Planos, novos contratos</p>
        </div>
        <!-- Financeiro - Amber -->
        <div
          class="p-3 rounded-lg border border-n-weak bg-n-amber-3 dark:bg-n-amber-3"
        >
          <div class="flex items-center gap-2 mb-1">
            <span class="i-lucide-wallet size-4 text-n-amber-11" />
            <span class="text-sm font-medium text-n-amber-11">Financeiro</span>
          </div>
          <input
            :value="workflowAgentsConfig.financeiro_name"
            type="text"
            placeholder="Nome"
            class="w-full px-0 py-0.5 text-xs bg-transparent border-0 border-b border-dashed border-n-amber-8/50 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-amber-8 font-medium"
            @input="
              updateWorkflowAgentName('financeiro_name', $event.target.value)
            "
          />
          <p class="text-xs text-n-slate-11 mt-2">
            Faturas, segunda via, desbloqueio
          </p>
        </div>
        <!-- Suporte - Blue -->
        <div
          class="p-3 rounded-lg border border-n-weak bg-n-blue-3 dark:bg-n-blue-3"
        >
          <div class="flex items-center gap-2 mb-1">
            <span class="i-lucide-wrench size-4 text-n-blue-11" />
            <span class="text-sm font-medium text-n-blue-11">Suporte</span>
          </div>
          <input
            :value="workflowAgentsConfig.suporte_name"
            type="text"
            placeholder="Nome"
            class="w-full px-0 py-0.5 text-xs bg-transparent border-0 border-b border-dashed border-n-blue-8/50 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-blue-8 font-medium"
            @input="
              updateWorkflowAgentName('suporte_name', $event.target.value)
            "
          />
          <p class="text-xs text-n-slate-11 mt-2">
            Problemas técnicos, conexão
          </p>
        </div>
      </div>
      <p class="mt-3 text-xs text-n-slate-11">
        {{ t('LIA_AGENTS.START.WORKFLOW_AGENTS_INFO') }}
      </p>
    </div>
    <!-- eslint-enable vue/no-bare-strings-in-template -->

    <!-- Agent Identity (LIA 2.1) -->
    <!-- eslint-disable vue/no-bare-strings-in-template -->
    <div v-if="isLia21" class="bg-n-solid-3 rounded-xl p-6">
      <h3
        class="text-lg font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
      >
        <span class="i-lucide-user-circle size-5 text-woot-500" />
        Identidade da Assistente
      </h3>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
            Nome
          </label>
          <input
            :value="agentConfig.name"
            type="text"
            placeholder="Ex: Lia, Ana, Sofia..."
            class="w-full px-4 py-2.5 bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500"
            @input="updateAgentConfig('name', $event.target.value)"
          />
          <p class="text-xs text-n-slate-11 mt-1">
            Usado no prompt e nas conversas.
          </p>
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
            Função
          </label>
          <div class="grid grid-cols-2 gap-1.5">
            <button
              v-for="role in roleOptions"
              :key="role.id"
              type="button"
              class="px-3 py-1.5 border rounded-lg transition-all text-left text-sm"
              :class="
                personaConfig.role === role.id
                  ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20 font-medium text-n-slate-12'
                  : 'border-n-weak text-n-slate-11 hover:border-n-strong'
              "
              @click="updatePersonaConfig('role', role.id)"
            >
              {{ role.label }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- eslint-enable vue/no-bare-strings-in-template -->

    <!-- Languages -->
    <div class="bg-n-solid-3 rounded-xl p-6">
      <h3
        class="text-lg font-semibold text-n-slate-12 mb-2 flex items-center gap-2"
      >
        <span class="i-lucide-languages size-5 text-woot-500" />
        {{ t('LIA_AGENTS.START.LANGUAGES_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mb-4">
        {{ t('LIA_AGENTS.START.LANGUAGES_SUBTITLE') }}
      </p>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        <button
          v-for="lang in languages"
          :key="lang.id"
          type="button"
          class="flex items-center gap-3 p-3 border-2 rounded-lg text-left transition-all"
          :class="[
            agentConfig.languages?.includes(lang.id)
              ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20'
              : 'border-n-weak hover:border-n-strong',
          ]"
          @click="toggleLanguage(lang.id)"
        >
          <span class="text-lg">{{ lang.flag }}</span>
          <span class="text-sm font-medium text-n-slate-12">{{
            lang.name
          }}</span>
          <span
            v-if="agentConfig.languages?.includes(lang.id)"
            class="i-lucide-check-circle size-4 text-woot-500 ml-auto"
          />
        </button>
      </div>
      <p class="mt-2 text-xs text-n-slate-11">
        {{
          t('LIA_AGENTS.START.LANGUAGES_COUNT', {
            count: agentConfig.languages?.length || 0,
          })
        }}
      </p>
    </div>

    <!-- Quick Stats -->
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
      <div class="bg-n-solid-3 rounded-xl p-4 border border-n-weak">
        <div class="flex items-center gap-2 mb-2">
          <span class="i-lucide-inbox size-4 text-n-slate-11" />
          <span class="text-xs text-n-slate-11">{{
            t('LIA_AGENTS.START.STATS_INBOXES')
          }}</span>
        </div>
        <p class="text-2xl font-bold text-n-slate-12">
          {{ agentConfig.inbox_ids?.length || 0 }}
        </p>
      </div>
      <div class="bg-n-solid-3 rounded-xl p-4 border border-n-weak">
        <div class="flex items-center gap-2 mb-2">
          <span class="i-lucide-globe size-4 text-n-slate-11" />
          <span class="text-xs text-n-slate-11">{{
            t('LIA_AGENTS.START.STATS_LANGUAGES')
          }}</span>
        </div>
        <p class="text-2xl font-bold text-n-slate-12">
          {{ agentConfig.languages?.length || 1 }}
        </p>
      </div>
      <div class="bg-n-solid-3 rounded-xl p-4 border border-n-weak">
        <div class="flex items-center gap-2 mb-2">
          <span class="i-lucide-flask-conical size-4 text-n-slate-11" />
          <span class="text-xs text-n-slate-11">{{
            t('LIA_AGENTS.START.STATS_TEST_MODE')
          }}</span>
        </div>
        <p
          class="text-lg font-bold"
          :class="testModeEnabled ? 'text-yellow-500' : 'text-green-500'"
        >
          {{
            testModeEnabled
              ? t('LIA_AGENTS.START.STATUS_ACTIVE')
              : t('LIA_AGENTS.START.MODE_PRODUCTION')
          }}
        </p>
      </div>
      <div class="bg-n-solid-3 rounded-xl p-4 border border-n-weak">
        <div class="flex items-center gap-2 mb-2">
          <span class="i-lucide-activity size-4 text-n-slate-11" />
          <span class="text-xs text-n-slate-11">{{
            t('LIA_AGENTS.START.STATS_STATUS')
          }}</span>
        </div>
        <p
          class="text-lg font-bold"
          :class="agent?.is_active ? 'text-green-500' : 'text-n-slate-11'"
        >
          {{
            agent?.is_active
              ? t('LIA_AGENTS.START.STATUS_ACTIVE')
              : t('LIA_AGENTS.START.STATUS_INACTIVE')
          }}
        </p>
      </div>
    </div>
  </div>
</template>
